import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

// Shared hover/focus tooltip: the lavender bubble with an arrow used across the
// educator dashboard. Extracted from the copies that had grown in EditGroupsPage
// (TitleTooltip), CreateGroupsBar and dashboard.css (.std-name-tooltip) so the bubble
// is described once.
//
// The bubble renders in a portal, fixed to the trigger's measured position, because
// its callers sit inside containers that legitimately clip: group cards live in an
// accordion whose `overflow: hidden` drives the expand/collapse animation. Absolute
// positioning would be cut off there.
//
// Wrap the trigger; the bubble shows on hover and on keyboard focus anywhere inside,
// so a focusable trigger gets it without a pointer. Pass the same `id` you put in the
// trigger's aria-describedby.

const Bubble = styled.span`
  background: ${(p) => p.$background};
  border-radius: 20px;
  box-shadow: ${(p) => (p.$shadow ? "0px 4px 12px rgba(14, 26, 49, 0.12)" : "none")};
  color: #00140b;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0;
  line-height: 20px;
  padding: 16px;
  /* The trigger may sit in uppercased or bolded text; the bubble sets its own. */
  font-style: normal;
  text-transform: none;
  position: fixed;
  width: max-content;
  max-width: ${(p) => p.$maxWidth}px;
  white-space: ${(p) => (p.$nowrap ? "nowrap" : "normal")};
  /* Above a Bootstrap modal (.modal 1055, .modal-backdrop 1050) and its popover /
     tooltip layers: tooltips are used inside modals (the copy-lesson class list),
     where 1000 put the bubble behind the dialog and nothing appeared. */
  z-index: 1100;

  left: ${(p) => p.$left}px;
  top: ${(p) => p.$top}px;
  transform: ${(p) => p.$transform};
`;

// Arrow as a CSS border triangle rather than an SVG, so one rule set covers every
// placement. Sized 16x12 across the base to match the design's arrow.
const Arrow = styled.span`
  border-style: solid;
  border-color: transparent;
  position: absolute;

  ${({ $placement, $align, $background }) => {
    if ($placement === "right") {
      // Half of the isosceles arrow, per the design: a flat top edge with the slant
      // running down to the point. Nudged off centre so the flat edge lines up with
      // the bubble's rounded corner.
      return `
        border-width: 0 16px 14px 0;
        border-right-color: ${$background};
        right: 100%;
        top: 50%;
        transform: translateY(-46%);
      `;
    }
    if ($placement === "left") {
      // Mirror of "right": the bubble sits left of the trigger, so the arrow hangs
      // off the bubble's right edge and points right.
      return `
        border-width: 0 0 14px 16px;
        border-left-color: ${$background};
        left: 100%;
        top: 50%;
        transform: translateY(-46%);
      `;
    }
    const across = $align === "center"
      ? "left: 50%; transform: translateX(-50%);"
      : "left: 16px;";
    return $placement === "top"
      ? `border-width: 12px 8px 0; border-top-color: ${$background}; top: 100%; ${across}`
      : `border-width: 0 8px 12px; border-bottom-color: ${$background}; bottom: 100%; ${across}`;
  }}
`;

// The frame's 16x12 arrow: a right triangle whose flat edge is on top
// and whose point faces the trigger. Mirrored when the bubble flips left.
const FRAME_ARROW_PATH = "M16 0V12L0 0H16Z";
// Below the trigger the same tail points up: apex and vertical edge on the
// left, the 16px base on the bubble's top edge, centred on the bubble.
const FRAME_ARROW_UP_PATH = "M0 0V12H16Z";

const FrameArrow = styled.svg`
  position: absolute;
  ${({ $placement }) => {
    if ($placement === "bottom") return "bottom: 100%; left: 50%; transform: translateX(-50%);";
    return $placement === "left"
      ? "top: 50%; left: 100%; transform: translateY(-50%) scaleX(-1);"
      : "top: 50%; right: 100%; transform: translateY(-50%);";
  }}
`;

const Wrap = styled.span`
  display: inline-flex;
  min-width: 0;
  position: relative;
`;

// Where the fixed bubble goes for a measured trigger rect. The result carries the
// placement actually used: "right" flips to "left" when the bubble would run off
// the viewport's right edge.
const place = (rect, placement, align, maxWidth, offset) => {
  if (placement === "right") {
    const top = rect.top + rect.height / 2;
    const fits = rect.right + 16 + maxWidth <= window.innerWidth;
    return fits
      ? { placement: "right", left: rect.right + 16, top, transform: "translateY(-50%)" }
      : { placement: "left", left: rect.left - 16, top, transform: "translate(-100%, -50%)" };
  }
  const left = align === "center" ? rect.left + rect.width / 2 : rect.left;
  const across = align === "center" ? "translateX(-50%)" : "none";
  return placement === "top"
    ? { left, top: rect.top - offset, transform: `${across === "none" ? "" : `${across} `}translateY(-100%)`.trim() }
    : { left, top: rect.bottom + offset, transform: across };
};

export const Tooltip = ({
  id,
  tip,
  children,
  // "top" / "bottom" sit the bubble above or below the trigger; "right" beside it,
  // vertically centred.
  placement = "top",
  // "center" centres on the trigger, "start" left-aligns to it. Ignored for "right".
  align = "center",
  maxWidth = 320,
  nowrap = false,
  background = "#eaeafc",
  // When true the bubble stays open while the pointer is over it, so the tip can
  // hold interactive content (a link/button). Default false keeps the plain
  // hover-to-show, leave-to-hide behaviour every existing caller relies on.
  interactive = false,
  // When true, Escape hides a showing bubble without moving focus or the
  // pointer (WCAG 1.4.13). Off by default so no existing caller changes.
  dismissOnEscape = false,
  // When true, the bubble is aria-hidden: for a trigger that already carries
  // the tip's full text, where announcing it again is noise.
  hiddenFromScreenReaders = false,
  // When true, a click (or tap) on the trigger toggles the bubble, and a
  // click-opened bubble stays until a second click, a mousedown outside, blur
  // or Escape; moving the pointer away does not hide it. Off by default.
  toggleOnClick = false,
  // "16x12" draws the frame's right-triangle arrow for a "right"/"left" or
  // "bottom" bubble. The default is the border arrow every existing caller has.
  arrowSize = null,
  // False drops the bubble's shadow, for a design that draws none. Default true
  // keeps the shadow every existing caller has.
  shadow = true,
  // A ref to the element the bubble is placed against, when that is not the
  // trigger itself (a bubble centred on a whole column rather than on its
  // text). Default: the trigger.
  anchorRef = null,
  // Gap between the anchor and a "top"/"bottom" bubble. Default 8, as before.
  offset = 8,
}) => {
  const wrapRef = useRef(null);
  const bubbleRef = useRef(null);
  const hideTimer = useRef(null);
  const [pos, setPos] = useState(null);
  const [pinned, setPinned] = useState(false);

  // Hides the bubble and forgets that a click opened it.
  const unpin = useCallback(() => {
    setPinned(false);
    setPos(null);
  }, []);

  const clearHideTimer = useCallback(() => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  }, []);

  // An interactive bubble's hide timer must not outlive the component: the
  // trigger can unmount inside the 120ms grace (a card collapsing), and the
  // timer would then set state on a dead component.
  useEffect(() => clearHideTimer, [clearHideTimer]);

  useEffect(() => {
    if (!dismissOnEscape || !pos) return undefined;
    const dismiss = (event) => {
      if (event.key === "Escape") unpin();
    };
    document.addEventListener("keydown", dismiss);
    return () => document.removeEventListener("keydown", dismiss);
  }, [dismissOnEscape, pos, unpin]);

  useEffect(() => {
    if (!pinned) return undefined;
    const closeFromOutside = (event) => {
      const inside = [wrapRef.current, bubbleRef.current].some((node) => node?.contains(event.target));
      if (!inside) unpin();
    };
    document.addEventListener("mousedown", closeFromOutside);
    return () => document.removeEventListener("mousedown", closeFromOutside);
  }, [pinned, unpin]);

  const show = useCallback(() => {
    clearHideTimer();
    const rect = (anchorRef?.current || wrapRef.current)?.getBoundingClientRect();
    if (rect) setPos(place(rect, placement, align, maxWidth, offset));
  }, [placement, align, maxWidth, offset, anchorRef, clearHideTimer]);

  const hide = useCallback(() => {
    if (!interactive) {
      setPos(null);
      return;
    }
    // Give the pointer time to cross the gap from trigger to bubble.
    clearHideTimer();
    hideTimer.current = setTimeout(() => setPos(null), 120);
  }, [interactive, clearHideTimer]);

  const toggle = useCallback(() => {
    if (pinned) {
      unpin();
      return;
    }
    setPinned(true);
    show();
  }, [pinned, show, unpin]);

  return (
    <Wrap
      ref={wrapRef}
      onMouseEnter={show}
      onMouseLeave={pinned ? undefined : hide}
      // Focus/blur bubble up from the trigger inside, so keyboard users get it too.
      onFocus={show}
      onBlur={pinned ? unpin : hide}
      onClick={toggleOnClick ? toggle : undefined}
    >
      {children}
      {tip && pos && createPortal(
        <Bubble
          ref={bubbleRef}
          id={id}
          role="tooltip"
          aria-hidden={hiddenFromScreenReaders ? "true" : undefined}
          data-placement={pos.placement || placement}
          $maxWidth={maxWidth}
          $nowrap={nowrap}
          $background={background}
          $shadow={shadow}
          $left={pos.left}
          $top={pos.top}
          $transform={pos.transform || "none"}
          onMouseEnter={interactive ? clearHideTimer : undefined}
          onMouseLeave={interactive ? hide : undefined}
        >
          {tip}
          {arrowSize === "16x12" ? (
            <FrameArrow
              $placement={pos.placement || placement}
              width="16"
              height="12"
              viewBox="0 0 16 12"
              fill="none"
              aria-hidden="true"
            >
              <path
                d={(pos.placement || placement) === "bottom" ? FRAME_ARROW_UP_PATH : FRAME_ARROW_PATH}
                fill={background}
              />
            </FrameArrow>
          ) : (
            <Arrow $placement={pos.placement || placement} $align={align} $background={background} aria-hidden="true" />
          )}
        </Bubble>,
        document.body,
      )}
    </Wrap>
  );
};
